import { useTranslation } from "react-i18next";
import type { FaqItem } from "../../constants/faq-items";
import { RevealOnView } from "../reveal-on-view";

interface FaqCardProps {
	question: string;
	answer: string;
}

function FaqCard({ question, answer }: FaqCardProps) {
	return (
		<article className="rounded-[1.75rem] border border-white/10 bg-white/6 p-6 shadow-[0_18px_50px_-40px_rgba(0,0,0,0.72)] backdrop-blur">
			<h3 className="text-base font-semibold text-white">{question}</h3>
			<p className="mt-3 text-sm leading-7 text-white/68">{answer}</p>
		</article>
	);
}

interface FaqSectionProps {
	items: FaqItem[];
	title?: string;
	intro?: string;
}

export function FaqSection({ items, title, intro }: FaqSectionProps) {
	const { t } = useTranslation("marketing");
	const effectiveTitle = title ?? t("faq_title");

	return (
		<section id="faq" className="scroll-mt-28 py-10">
			<RevealOnView from="up">
				<div className="max-w-3xl">
					<p className="text-sm font-semibold uppercase tracking-[0.24em] text-app-accent-secondary">{t("faq_eyebrow")}</p>
					<h2 className="mt-4 text-4xl font-semibold tracking-[-0.06em] text-white sm:text-5xl">{effectiveTitle}</h2>
					{intro ? <p className="mt-5 text-lg leading-8 text-white/68">{intro}</p> : null}
				</div>
			</RevealOnView>
			<div className="mt-12 grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
				{items.map((item, index) => (
					<RevealOnView key={item.question} delayMs={index * 45} from="up">
						<FaqCard question={item.question} answer={item.answer} />
					</RevealOnView>
				))}
			</div>
		</section>
	);
}
